import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiWifi, FiCoffee, FiDroplet, FiWind } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Accommodations = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const suites = [
    {
      name: "The Planter's Suite",
      description: "A spacious retreat with panoramic views of the Rakwana mountains. Features a private balcony and traditional Kandyan architecture elements.",
      amenities: ["King bed with organic cotton linens", "Private mountain-view balcony", "Traditional clay water vessels", "Rainfall shower with spring water"],
      image: "🏔️"
    },
    {
      name: "The Rainforest Wing",
      description: "Immerse yourself in nature's symphony. This suite opens directly to the forest canopy, perfect for dawn birdwatching.",
      amenities: ["Queen bed with mosquito netting", "Floor-to-ceiling forest views", "Natural ventilation system", "Outdoor meditation space"],
      image: "🌿"
    }
  ];

  const luxuries = [
    { icon: FiWifi, title: "Reliable Connectivity", description: "High-speed internet in common areas" },
    { icon: FiCoffee, title: "Estate Tea Service", description: "Organic Ceylon tea from local gardens" },
    { icon: FiDroplet, title: "Spring-Fed Water", description: "Pure mountain spring water throughout" },
    { icon: FiWind, title: "Natural Climate", description: "Cool mountain air, minimal AC needed" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-white to-mist-grey relative overflow-hidden">
      {/* Floating elements */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-32 h-32 rounded-full bg-terracotta-earth/10"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              scale: 0
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 25 + Math.random() * 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-4xl md:text-5xl font-playfair font-bold text-forest-green mb-6"
          >
            The Sanctuary
          </motion.h2>
          
          <motion.div 
            variants={itemVariants}
            className="w-24 h-1 bg-terracotta-earth mx-auto mb-8"
          ></motion.div>
          
          <motion.p 
            variants={itemVariants}
            className="text-xl font-lato text-gray-700 max-w-3xl mx-auto leading-relaxed"
          >
            Two distinctive suites designed for privacy and connection with nature. 
            Each space reflects the heritage of the region while providing modern comfort.
          </motion.p>
        </motion.div>

        {/* Suites */}
        <motion.div
          variants={containerVariants}
          className="grid lg:grid-cols-2 gap-12 mb-20"
        >
          {suites.map((suite, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="glass-card p-8 group"
            >
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{suite.image}</div>
                <h3 className="text-2xl font-playfair font-semibold text-forest-green mb-4">
                  {suite.name}
                </h3>
              </div>

              <p className="text-gray-700 font-lato mb-6 leading-relaxed">
                {suite.description}
              </p>

              <ul className="space-y-3">
                {suite.amenities.map((amenity, amenityIndex) => (
                  <li key={amenityIndex} className="flex items-start text-gray-600 font-lato">
                    <span className="w-2 h-2 bg-terracotta-earth rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    {amenity}
                  </li>
                ))}
              </ul>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-6 w-full glass-button py-3 text-forest-green font-lato font-medium"
              >
                View Suite Details
              </motion.button>
            </motion.div>
          ))}
        </motion.div>

        {/* Luxuries */}
        <motion.div
          variants={containerVariants}
          className="text-center mb-12"
        >
          <motion.h3 
            variants={itemVariants}
            className="text-3xl font-playfair font-semibold text-forest-green mb-8"
          >
            Villa Luxuries
          </motion.h3>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {luxuries.map((luxury, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              className="text-center p-6 rounded-2xl bg-white/50 backdrop-blur-sm border border-white/20"
            >
              <motion.div
                whileHover={{ rotate: 10 }}
                className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest-green/10 mb-4"
              >
                <SafeIcon 
                  icon={luxury.icon} 
                  className="w-6 h-6 text-forest-green" 
                />
              </motion.div>

              <h4 className="text-lg font-playfair font-semibold text-forest-green mb-2">
                {luxury.title}
              </h4>

              <p className="text-gray-600 font-lato text-sm">
                {luxury.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Accommodations;